import type { PaginatedResponse, WorkfrontScopeKey } from '@atlas/types'
import { WorkfrontBundleSyncStatus, WorkfrontSyncErrorStatus } from '@atlas/types'
import { type QueryClient, type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type BundleListParams,
	type ErrorListParams,
	type RunListParams,
	type WorkfrontBundleDetailDTO,
	type WorkfrontBundleDTO,
	type WorkfrontErrorDTO,
	type WorkfrontScopeEntryDTO,
	workfrontApi,
} from '@/services/workfront.service'

/**
 * React-query hooks for Admin › Workfront. Every key starts with `workfront`, so
 * one invalidation refreshes the whole area after anything that changes it.
 */
export const WORKFRONT_KEY = ['workfront'] as const
const keys = {
	overview: [...WORKFRONT_KEY, 'overview'] as const,
	health: [...WORKFRONT_KEY, 'health'] as const,
	scope: [...WORKFRONT_KEY, 'scope'] as const,
	bundles: [...WORKFRONT_KEY, 'bundles'] as const,
	bundle: (id: string) => [...WORKFRONT_KEY, 'bundle', id] as const,
	runs: [...WORKFRONT_KEY, 'runs'] as const,
	run: (id: string) => [...WORKFRONT_KEY, 'run', id] as const,
	errors: [...WORKFRONT_KEY, 'errors'] as const,
	error: (id: string) => [...WORKFRONT_KEY, 'error', id] as const,
}

/** While something is moving (a bundle fetching, a run running), poll at this pace; otherwise stay put. */
const LIVE_REFRESH_MS = 5_000

const invalidateAll = (qc: QueryClient) => qc.invalidateQueries({ queryKey: WORKFRONT_KEY })

export const useWorkfrontOverview = (options: { enabled?: boolean } = {}) =>
	useQuery({ queryKey: keys.overview, queryFn: workfrontApi.overview, staleTime: 15_000, ...options })

export const useWorkfrontHealth = () =>
	useQuery({ queryKey: keys.health, queryFn: workfrontApi.health, staleTime: 60_000, retry: false })

export const useWorkfrontScope = () => useQuery({ queryKey: keys.scope, queryFn: workfrontApi.listScope })

/** Set one scope key (a switch): shown at once, put back if the write fails. */
export const useSetWorkfrontScope = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ key, value }: { key: WorkfrontScopeKey; value: unknown }) => workfrontApi.setScope(key, value),
		onMutate: async ({ key, value }: { key: WorkfrontScopeKey; value: unknown }) => {
			await qc.cancelQueries({ queryKey: keys.scope })
			const snapshot = qc.getQueryData<WorkfrontScopeEntryDTO[]>(keys.scope)
			qc.setQueryData<WorkfrontScopeEntryDTO[]>(keys.scope, (entries) =>
				entries?.map((entry) => (entry.key === key ? { ...entry, value, isDefault: false } : entry)),
			)
			return { snapshot }
		},
		onError: (_error, _vars, context) => qc.setQueryData(keys.scope, context?.snapshot),
		onSettled: () => invalidateAll(qc),
	})
}

export const useWorkfrontBundles = (params: BundleListParams) =>
	useQuery({
		queryKey: [...keys.bundles, params],
		queryFn: () => workfrontApi.listBundles(params),
		staleTime: 10_000,
		refetchInterval: (query) =>
			query.state.data?.data.some((bundle) => bundle.syncStatus === WorkfrontBundleSyncStatus.FETCHING)
				? LIVE_REFRESH_MS
				: false,
	})

export const useWorkfrontBundle = (id: string) =>
	useQuery({
		queryKey: keys.bundle(id),
		queryFn: () => workfrontApi.getBundle(id),
		refetchInterval: (query) =>
			query.state.data?.syncStatus === WorkfrontBundleSyncStatus.FETCHING ? LIVE_REFRESH_MS : false,
	})

export const useWorkfrontRuns = (params: RunListParams) =>
	useQuery({ queryKey: [...keys.runs, params], queryFn: () => workfrontApi.listRuns(params), staleTime: 10_000 })

export const useWorkfrontRun = (id: string) =>
	useQuery({
		queryKey: keys.run(id),
		queryFn: () => workfrontApi.getRun(id),
		refetchInterval: (query) => (query.state.data?.finishedAt === null ? LIVE_REFRESH_MS : false),
	})

export const useWorkfrontErrors = (params: ErrorListParams) =>
	useQuery({ queryKey: [...keys.errors, params], queryFn: () => workfrontApi.listErrors(params), staleTime: 10_000 })

export const useWorkfrontError = (id: string) =>
	useQuery({ queryKey: keys.error(id), queryFn: () => workfrontApi.getError(id) })

/** Sync one bundle now. It queues a fetch: there is nothing to patch until the bundle exists, so it only invalidates. */
export const useSyncWorkfrontBundle = () => {
	const qc = useQueryClient()
	return useMutation({ mutationFn: workfrontApi.syncBundle, onSettled: () => invalidateAll(qc) })
}

/** Start a discovery run now; it queues work, so it only invalidates. */
export const useRunWorkfrontDiscovery = () => {
	const qc = useQueryClient()
	return useMutation({ mutationFn: workfrontApi.runDiscovery, onSettled: () => invalidateAll(qc) })
}

/** Refresh the reference data now; it queues work, so it only invalidates. */
export const useRefreshWorkfrontReference = () => {
	const qc = useQueryClient()
	return useMutation({ mutationFn: workfrontApi.refreshReference, onSettled: () => invalidateAll(qc) })
}

type Snapshot = [QueryKey, unknown][]

/** Snapshots every cached entry under `prefix`, after stopping refetches that could overwrite a patch. */
async function snapshotOf(qc: QueryClient, prefix: QueryKey): Promise<Snapshot> {
	await qc.cancelQueries({ queryKey: prefix })
	return qc.getQueriesData({ queryKey: prefix })
}

const restore = (qc: QueryClient, snapshot: Snapshot | undefined) => {
	for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data)
}

/** Refetch a bundle: shown `fetching` at once in its detail and in every bundles page. */
export const useRefetchWorkfrontBundle = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: workfrontApi.refetchBundle,
		onMutate: async (id: string) => {
			const snapshot = await snapshotOf(qc, WORKFRONT_KEY)
			const fetching = { syncStatus: WorkfrontBundleSyncStatus.FETCHING }
			qc.setQueryData<WorkfrontBundleDetailDTO>(keys.bundle(id), (bundle) =>
				bundle ? { ...bundle, ...fetching } : bundle,
			)
			qc.setQueriesData<PaginatedResponse<WorkfrontBundleDTO>>({ queryKey: keys.bundles }, (page) =>
				page
					? { ...page, data: page.data.map((bundle) => (bundle.id === id ? { ...bundle, ...fetching } : bundle)) }
					: page,
			)
			return { snapshot }
		},
		onError: (_error, _id, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateAll(qc),
	})
}

/** Patches one error everywhere it is cached: its detail, every errors page, and the bundle it sits on. */
function patchError(qc: QueryClient, id: string, patch: Partial<WorkfrontErrorDTO>) {
	const apply = (error: WorkfrontErrorDTO) => (error.id === id ? { ...error, ...patch } : error)
	qc.setQueryData<WorkfrontErrorDTO>(keys.error(id), (error) => (error ? apply(error) : error))
	qc.setQueriesData<PaginatedResponse<WorkfrontErrorDTO>>({ queryKey: keys.errors }, (page) =>
		page ? { ...page, data: page.data.map(apply) } : page,
	)
	qc.setQueriesData<WorkfrontBundleDetailDTO>({ queryKey: [...WORKFRONT_KEY, 'bundle'] }, (bundle) =>
		bundle ? { ...bundle, errors: bundle.errors.map(apply) } : bundle,
	)
}

/** Retry an error: shown `retrying` at once; the error resolves itself when its steps succeed. */
export const useRetryWorkfrontError = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: workfrontApi.retryError,
		onMutate: async (id: string) => {
			const snapshot = await snapshotOf(qc, WORKFRONT_KEY)
			patchError(qc, id, { status: WorkfrontSyncErrorStatus.RETRYING })
			return { snapshot }
		},
		onError: (_error, _id, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateAll(qc),
	})
}

/** Ignore an error with a reason: shown `ignored`, with the reason, at once. */
export const useIgnoreWorkfrontError = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) => workfrontApi.ignoreError(id, reason),
		onMutate: async ({ id, reason }: { id: string; reason: string }) => {
			const snapshot = await snapshotOf(qc, WORKFRONT_KEY)
			patchError(qc, id, { status: WorkfrontSyncErrorStatus.IGNORED, resolutionNote: reason })
			return { snapshot }
		},
		onError: (_error, _vars, context) => restore(qc, context?.snapshot),
		onSettled: () => invalidateAll(qc),
	})
}
